import { subMonths, subSeconds } from 'date-fns';

const hasText = (value) =>
  typeof value === 'string' && value.trim().length > 0;

const hasItems = (value) =>
  Array.isArray(value) &&
  value.some((item) => {
    if (typeof item === 'string') return hasText(item);
    if (typeof item === 'number') return true;
    return Boolean(item?.id || item?.value || item?.name || item?.label);
  });

const hasCompleteService = (services) =>
  services?.some(
    ({ name, description, _destroy: destroy }) =>
      !destroy && hasText(name) && hasText(description)
  );

const hasCompleteExperience = (experiences) =>
  experiences?.some(({ _destroy: destroy }) => !destroy);

const hasExperienceWebsite = (experiences) =>
  experiences?.some(
    ({ company_url: companyUrl, _destroy: destroy }) =>
      !destroy && hasText(companyUrl)
  );

const includesArea = (profile, terms) =>
  (profile.industry_areas || []).some((area) => {
    const value = String(
      area?.name || area?.label || area?.value || area
    ).toLowerCase();
    return terms.some((term) => value.includes(term));
  });

export const isCreativeProfile = (profile) =>
  (profile.industry_areas || []).some((area) => {
    const value = String(area?.name || area?.label || area?.value || area)
      .trim()
      .toLowerCase();
    return value === 'creative / artist';
  });

const isLiveEventsProfile = (profile) =>
  includesArea(profile, ['live', 'event', 'venue']);

const isOneMonthOld = (value, now = new Date()) => {
  if (!value) return false;

  const remindedAt = new Date(value);
  if (Number.isNaN(remindedAt.getTime())) return true;

  // const oneMonthAgo = subMonths(now, 1);
  const oneMonthAgo = subSeconds(now, 120); 
  return remindedAt <= oneMonthAgo;
};

export const ONBOARDING_STATUSES = {
  REQUIRED: 'required',
  DUE: 'due',
  NOT_DUE: 'not_due',
  COMPLETED: 'completed',
};

export const createOnboardingUpdateConfig = (
  isComplete,
  now = new Date()
) => ({
  last_remind_at: isComplete ? null : now.toISOString(),
});

export const CREATIVE_CONDITIONAL_FIELD_KEYS = [
  'professional_roles',
  'interest_topics',
  'rep_artists',
  'spotify_playlists',
  'youtube_playlists',
];

export const STEP_DEFINITIONS = [
  {
    key: 'about_you',
    eyebrow: 'Tell us about yourself',
    title: 'About you',
    description:
      'Add the essentials that help other members understand who you are.',
  },
  {
    key: 'work',
    eyebrow: 'Show what you do and where you want to go',
    title: 'Your work & goals',
    description:
      'Share your experience, services, goals, and the markets you want to reach.',
  },
  {
    key: 'connect',
    eyebrow: 'Personalise your GLP experience',
    title: 'Interests & links',
    description:
      'Add links and interests so we can recommend more relevant people and events.',
  },
];

export const PROFILE_FIELD_RULES = [
  {
    key: 'profile_photo',
    step: 'about_you',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasText(profile.image_url || profile.avatarUrl),
  },
  {
    key: 'city',
    step: 'about_you',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    isComplete: (profile) => Boolean(profile.city?.id || profile.city?.name || profile.city),
  },
  {
    key: 'industry_since',
    step: 'about_you',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    isComplete: (profile) => Boolean(profile.industry_since),
  },
  {
    key: 'member_types',
    step: 'about_you',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasItems(profile.member_types),
  },
  {
    key: 'biography',
    step: 'about_you',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasText(profile.biography),
  },
  {
    key: 'languages',
    step: 'about_you',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasItems(profile.languages),
  },
  {
    key: 'services',
    step: 'work',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasCompleteService(profile.users_services),
  },
  {
    key: 'industry_areas',
    step: 'work',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    // alwaysShowInPeriodicReview: true,
    isComplete: (profile) => hasItems(profile.industry_areas),
  },
  {
    key: 'professional_goals',
    step: 'work',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    // alwaysShowInPeriodicReview: true,
    isComplete: (profile) => hasItems(profile.professional_goals),
  },
  {
    key: 'target_markets',
    step: 'work',
    requiredOnFirstLogin: true,
    includeInPeriodicReview: true,
    // alwaysShowInPeriodicReview: true,
    isComplete: (profile) => hasItems(profile.target_markets),
  },
  {
    key: 'work_experience',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasCompleteExperience(profile.users_experiences),
  },
  {
    key: 'website',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasExperienceWebsite(profile.users_experiences),
  },
  {
    key: 'professional_skills',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    // alwaysShowInPeriodicReview: true,
    isComplete: (profile) => hasItems(profile.professional_skills),
  },
  {
    key: 'professional_roles',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isRelevant: isCreativeProfile,
    isComplete: (profile) => hasItems(profile.professional_roles),
  },
  {
    key: 'interest_topics',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isRelevant: isCreativeProfile,
    isComplete: (profile) => hasItems(profile.interest_topics),
  },
  {
    key: 'rep_artists',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isRelevant: isCreativeProfile,
    isComplete: (profile) => hasItems(profile.rep_artists),
  },
  {
    key: 'rep_events',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isRelevant: isLiveEventsProfile,
    isComplete: (profile) => hasItems(profile.rep_events),
  },
  {
    key: 'rep_venues',
    step: 'work',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isRelevant: isLiveEventsProfile,
    isComplete: (profile) => hasItems(profile.rep_venues),
  },
  ...['linked_in', 'instagram', 'facebook'].map((key) => ({
    key,
    step: 'connect',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isComplete: (profile) => hasText(profile[key]),
  })),
  {
    key: 'spotify_playlists',
    step: 'connect',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isRelevant: isCreativeProfile,
    isComplete: (profile) => hasItems(profile.spotify_playlists),
  },
  {
    key: 'youtube_playlists',
    step: 'connect',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    isRelevant: isCreativeProfile,
    isComplete: (profile) => hasItems(profile.youtube_playlists),
  },
  {
    key: 'music_genres',
    step: 'connect',
    requiredOnFirstLogin: false,
    includeInPeriodicReview: true,
    // alwaysShowInPeriodicReview: true,
    isComplete: (profile) => hasItems(profile.music_genres),
  },
  ...['event_types', 'event_formats', 'event_attend_locations'].map(
    (key) => ({
      key,
      step: 'connect',
      requiredOnFirstLogin: false,
      includeInPeriodicReview: true,
      isComplete: (profile) => hasItems(profile[key]),
    })
  ),
];

export const getOnboardingStatus = (profile, now = new Date()) => {
  if (profile?.completed_profile === true) {
    return ONBOARDING_STATUSES.COMPLETED;
  }

  if (!profile?.last_remind_at) {
    return ONBOARDING_STATUSES.REQUIRED;
  }

  return isOneMonthOld(profile.last_remind_at, now)
    ? ONBOARDING_STATUSES.DUE
    : ONBOARDING_STATUSES.NOT_DUE;
};

export const getMissingFieldKeys = (profile, status) => {
  if (
    ![ONBOARDING_STATUSES.REQUIRED, ONBOARDING_STATUSES.DUE].includes(status)
  ) {
    return [];
  }

  return PROFILE_FIELD_RULES.filter((field) => {
    const shouldCheck =
      status === ONBOARDING_STATUSES.REQUIRED
        ? field.requiredOnFirstLogin
        : field.includeInPeriodicReview;
    const isRelevant = !field.isRelevant || field.isRelevant(profile);
    const shouldShow =
      (status === ONBOARDING_STATUSES.DUE &&
        field.alwaysShowInPeriodicReview) ||
      !field.isComplete(profile);

    return shouldCheck && isRelevant && shouldShow;
  }).map(({ key }) => key);
};

export const getVisibleSteps = (fieldKeys) =>
  STEP_DEFINITIONS.map((step) => ({
    ...step,
    fieldKeys: PROFILE_FIELD_RULES.filter(
      (field) => field.step === step.key && fieldKeys.includes(field.key)
    ).map(({ key }) => key),
  })).filter(({ fieldKeys: keys }) => keys.length > 0);

export const isProfileComplete = (profile) =>
  PROFILE_FIELD_RULES.every(
    (field) =>
      (field.isRelevant && !field.isRelevant(profile)) || field.isComplete(profile)
  );

export const createPeriodicPreviewProfile = (profile = {}) => ({
  ...profile,
  completed_profile: false,
  last_remind_at: '2020-01-01T00:00:00.000Z',
  image_url: profile.image_url || 'periodic-onboarding-preview',
  city: profile.city || { id: 'preview-city', name: 'Preview City' },
  industry_since: profile.industry_since || 2020,
  member_types: hasItems(profile.member_types)
    ? profile.member_types
    : [{ id: 'preview-member-type', name: 'Music Professional' }],
  users_services: hasCompleteService(profile.users_services)
    ? profile.users_services
    : [
        {
          id: 'preview-service',
          name: 'Music services',
          description: 'Preview service',
        },
      ],
  industry_areas: hasItems(profile.industry_areas)
    ? profile.industry_areas
    : ['Creative / Artist'],
  professional_goals: hasItems(profile.professional_goals)
    ? profile.professional_goals
    : ['Build connections'],
  target_markets: hasItems(profile.target_markets)
    ? profile.target_markets
    : ['United Kingdom'],
});
